import {
  type AiPreferences,
  AiPreferencesSchema,
  type AiSideEffects,
} from '@timelyapp/integration-cloud-api-spec';

const EMPTY_OBJECT = {} as const;

// Preset values mapping for fill_gap and min_duration
const PRESET_VALUES: Partial<
  Record<keyof AiPreferences['preset'], AiPreferences[keyof AiPreferences]>
> = {
  exact: { fill_gap: 1, min_duration: 1 },
  efficient: { fill_gap: 10, min_duration: 5 },
  concise: { fill_gap: 30, min_duration: 10 },
};

export type CoerceAiPreferencesReturn = {
  preferences: AiPreferences;
  sideEffects: AiSideEffects;
};

/**
 * Force AI preferences to be in a consistent state.
 * @returns potential `sideEffects` of the coercion
 */
export const coerceAiPreferences = (
  /** While waiting for XHR load, we need defaults coerced, hence optional */
  _preferences?: AiPreferences,
  existingPreferences?: AiPreferences
): CoerceAiPreferencesReturn => {
  // Start with the original _preferences
  const result = { enabled: false, preset: 'efficient', ..._preferences };
  const { enabled, preset } = result;

  // Apply preset-specific values if enabled and preset is selected
  if (enabled && preset && preset in PRESET_VALUES) {
    Object.assign(result, PRESET_VALUES[preset as keyof typeof PRESET_VALUES]);
  }

  const preferences = AiPreferencesSchema.parse({
    ...result,
    // Force timeline predictions on if AI is enabled
    show_timeline_predictions: enabled,
  });

  // Determine any side-effects
  let aiSuggestionsMustBeRefreshed = false;
  let aiModelMustBeRetrained = false;
  if (existingPreferences) {
    const changeComparison = { previous: existingPreferences, current: preferences };

    aiSuggestionsMustBeRefreshed =
      (_hasChanged('enabled', changeComparison) && preferences.enabled) ||
      _hasChanged('preset', changeComparison) ||
      _hasChanged('fill_gap', changeComparison) ||
      _hasChanged('min_duration', changeComparison);

    aiModelMustBeRetrained = _hasChanged('train_tags', changeComparison);
  }

  return {
    preferences,
    sideEffects: {
      aiModelMustBeRetrained,
      aiSuggestionsMustBeRefreshed,
    },
  };
};

/** Check if a preference property has changed value */
const _hasChanged = (
  property: keyof AiPreferences,
  comparisons: {
    previous: AiPreferences;
    current: AiPreferences;
  }
): boolean => {
  return comparisons.previous?.[property] !== comparisons.current?.[property];
};
